// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { AnalyticsContext, IDineoutCartResponseData, IResponseCartItem } from "@swiggy-private/checkout-core";

// analytics
import type { IImpressionTurboCheckout, IClickTurboCheckout } from "@dineout-cart-client/interfaces/Cart";

// analytics interfaces
import {
    clickBillDetailsBanner,
    clickBillDetailsBannerHalfcard,
    clickBillViewChevron,
    clickEligibleOutlets,
    clickStealStoreCartBack,
    clickStealStoreDeleteItem,
    clickStealStoreItem,
    clickTurboCheckout,
    clickTurboPaymentMethodChange,
    impressionBillDetailsBanner,
    impressionEligibleOutlets,
    impressionStealStoreCart,
    impressionBillDetailsBannerHalfcard,
    impressionBillViewChevron,
    impressionCancellationPolicyWidget,
    impressionCollapsibleBillUpsell,
    impressionDeliveryFeeLineItem,
    impressionDeliveryFeeUnCollapsedLineItem,
    impressionSavingsBanner,
    impressionTaxesAndChargesLineItem,
    impressionTurboCheckout,
    impressionTurboPayBtn,
} from "@dineout-cart-client/analytics/events";
import type { ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";
import { getCartId, isCartIdSameAsPreviousCartId } from "@dineout-cart-client/helpers/cartHelper";
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";
import { Storage } from "@swiggy-private/core-ui";
import { PREVIOUS_CART_ID } from "@dineout-cart-client/constants";

export const useStealDealAnalytics = (cartResponse?: IDineoutCartResponseData | null) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const cartAnalyticsData = useMemo((): {
        voucherList: IResponseCartItem[];
        originalBill: number;
        finalBill: number;
        savedAmount: number;
    } | null => {
        if (isEmpty(cartResponse)) return null;

        const voucherList =
            cartResponse?.groupedItems?.flatMap((groupItem) => groupItem.items ?? []).map((item) => item) ?? [];
        const { dineoutPricingInfo } = cartResponse?.bill ?? {};

        return {
            voucherList,
            originalBill: dineoutPricingInfo?.totalBill ?? 0,
            finalBill: dineoutPricingInfo?.billToPay ?? 0,
            savedAmount: dineoutPricingInfo?.totalDiscount ?? 0,
        };
    }, [cartResponse]);

    const onImpressionCancellationPolicyWidget = useCallback(() => {
        if (!isEmpty(cartResponse)) {
            impressionCancellationPolicyWidget(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onimpressionSavingsBanner = useCallback(() => {
        if (!isEmpty(cartResponse)) {
            impressionSavingsBanner(impressionEvent, cartResponse?.bill?.dineoutPricingInfo?.billToPay);
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionBillViewChevron = useCallback(
        (context: Record<string, unknown>) => {
            if (!isEmpty(cartResponse)) {
                impressionBillViewChevron(impressionEvent, getCartId(cartResponse), context);
            }
        },
        [impressionEvent, cartResponse],
    );

    const onClickBillViewChevron = useCallback(
        (context: Record<string, unknown>) => {
            if (!isEmpty(cartResponse)) {
                clickBillViewChevron(clickEvent, getCartId(cartResponse), context);
            }
        },
        [clickEvent, cartResponse],
    );

    const onImpressionCollapsibleBillUpsell = useCallback(
        (upsellMessage: string) => {
            if (!isEmpty(cartResponse)) {
                impressionCollapsibleBillUpsell(impressionEvent, getCartId(cartResponse), upsellMessage);
            }
        },
        [impressionEvent, cartResponse],
    );

    const onImpressionDeliveryFeeLineItem = useCallback(
        (label: string): void => {
            try {
                const cartId = getCartId(cartResponse);
                const newLabel = { ...(safeJsonParse(label) as object), cartId };

                if (!isCartIdSameAsPreviousCartId(String(cartId))) {
                    impressionDeliveryFeeLineItem(impressionEvent, JSON.stringify(newLabel));
                }
                Storage.setItem(PREVIOUS_CART_ID, String(cartId));
            } catch {
                void 0;
            }
        },
        [impressionEvent, cartResponse],
    );

    const onImpressionTaxesAndChargesLineItem = useCallback(
        (context: string): void => {
            if (!isEmpty(cartResponse) && cartResponse) {
                impressionTaxesAndChargesLineItem(impressionEvent, context, getCartId(cartResponse));
            }
        },
        [cartResponse, impressionEvent],
    );

    const onImpressionDeliveryFeeUnCollapsedLineItem = useCallback(
        (label: string): void => {
            try {
                const cartId = getCartId(cartResponse);
                const newLabel = { details: safeJsonParse(label), cartId };

                impressionDeliveryFeeUnCollapsedLineItem(impressionEvent, JSON.stringify(newLabel));
            } catch {
                void 0;
            }
        },
        [impressionEvent, cartResponse],
    );

    const onImpressionBanner = useCallback(
        (bannerType?: string) => {
            impressionBillDetailsBanner(impressionEvent, getCartId(cartResponse), bannerType);
        },
        [impressionEvent, cartResponse],
    );

    const onClickBanner = useCallback(
        (bannerType?: string) => {
            clickBillDetailsBanner(clickEvent, getCartId(cartResponse), bannerType);
        },
        [clickEvent, cartResponse],
    );

    const onImpressionBannerHalfcard = useCallback(
        (bannerType?: string) => {
            impressionBillDetailsBannerHalfcard(impressionEvent, getCartId(cartResponse), bannerType);
        },
        [impressionEvent, cartResponse],
    );

    const onClickCloseBannerHalfcard = useCallback(
        (bannerType?: string) => {
            clickBillDetailsBannerHalfcard(clickEvent, getCartId(cartResponse), bannerType);
        },
        [clickEvent, cartResponse],
    );

    const onClickStealStoreCartBack = useCallback(() => {
        clickStealStoreCartBack(clickEvent, getCartId(cartResponse));
    }, [clickEvent, cartResponse]);

    const onClickTicketItem = useCallback(
        (item: ILocalCartItem) => {
            if (!isEmpty(cartResponse)) {
                clickStealStoreItem(clickEvent, item);
            }
        },
        [clickEvent, cartResponse],
    );

    const onClickDeleteTicketItem = useCallback(
        (item: ILocalCartItem) => {
            if (!isEmpty(cartResponse)) {
                clickStealStoreDeleteItem(clickEvent, item);
            }
        },
        [clickEvent, cartResponse],
    );

    const onImpressionEligibleOutletsForItems = useCallback(
        (items: Array<{ eligibleOutlets?: string[]; itemId?: string }>) => {
            if (isEmpty(cartResponse)) return;
            items.forEach((item) => {
                impressionEligibleOutlets(impressionEvent, item);
            });
        },
        [impressionEvent, cartResponse],
    );

    const onClickEligibleOutlets = useCallback(
        (eligibleOutlets?: string[], itemId?: string) => {
            if (!isEmpty(cartResponse)) {
                clickEligibleOutlets(clickEvent, eligibleOutlets, getCartId(cartResponse), itemId);
            }
        },
        [clickEvent, cartResponse],
    );

    const onImpressionStealStoreCart = useCallback(() => {
        impressionStealStoreCart(impressionEvent, cartAnalyticsData);
    }, [impressionEvent, cartAnalyticsData, cartResponse]);

    const onImpressionTurboCheckout = useCallback(
        (prop: IImpressionTurboCheckout) => {
            impressionTurboCheckout(impressionEvent, {
                ...prop,
                finalBill: cartAnalyticsData?.finalBill,
                savedAmount: cartAnalyticsData?.savedAmount,
            });
        },
        [impressionEvent, cartAnalyticsData],
    );

    const onClickTurboPaymentMethodChange = useCallback(
        (prop: IClickTurboCheckout) => {
            clickTurboPaymentMethodChange(clickEvent, {
                ...prop,
                savedAmount: cartAnalyticsData?.savedAmount,
            });
        },
        [clickEvent, cartAnalyticsData],
    );

    const onImpressionTurboPayBtn = useCallback(
        (prop: IImpressionTurboCheckout) => {
            impressionTurboPayBtn(impressionEvent, {
                ...prop,
                finalBill: cartAnalyticsData?.finalBill,
                savedAmount: cartAnalyticsData?.savedAmount,
            });
        },
        [impressionEvent, cartAnalyticsData],
    );

    const onClickTurboCheckout = useCallback(
        (prop: IClickTurboCheckout) => {
            clickTurboCheckout(clickEvent, {
                ...prop,
                finalBill: cartAnalyticsData?.finalBill,
                savedAmount: cartAnalyticsData?.savedAmount,
            });
        },
        [clickEvent, cartAnalyticsData],
    );

    return {
        onImpressionCancellationPolicyWidget,
        onimpressionSavingsBanner,
        onImpressionBillViewChevron,
        onClickBillViewChevron,
        onImpressionCollapsibleBillUpsell,
        onImpressionDeliveryFeeLineItem,
        onImpressionTaxesAndChargesLineItem,
        onImpressionDeliveryFeeUnCollapsedLineItem,
        onImpressionBanner,
        onClickBanner,
        onImpressionBannerHalfcard,
        onClickCloseBannerHalfcard,
        onClickStealStoreCartBack,
        onClickTicketItem,
        onClickDeleteTicketItem,
        onImpressionEligibleOutletsForItems,
        onClickEligibleOutlets,
        onImpressionStealStoreCart,
        onImpressionTurboCheckout,
        onClickTurboPaymentMethodChange,
        onImpressionTurboPayBtn,
        onClickTurboCheckout,
    };
};
